import {
  DEFAULT_PERMALINKS,
  PERMALINK_PAGE_TYPES,
  type PermalinkPageType,
  type Permalinks,
} from "./permalinks.dto";

/**
 * Storefront addresses from an agency's permalink words (feature: permalinks,
 * AC-03, AC-04). The one place a page-type word turns into a path — every
 * storefront link, the sitemap, emails and the dashboard build through here
 * rather than writing `/tours/` by hand.
 *
 * `links` is optional so a payload from an older producer, or a caller with no
 * agency at hand, reads as the defaults.
 */

type Links = Partial<Permalinks> | null | undefined;

/** A stored set may be null or partial (an agency that never saved); gaps are defaults (AC-01). */
export type StoredPermalinks = Partial<Record<string, string>> | null;

export function resolvePermalinks(
  stored: StoredPermalinks | undefined,
): Permalinks {
  const resolved = { ...DEFAULT_PERMALINKS };
  for (const type of PERMALINK_PAGE_TYPES) {
    const word = stored?.[type];
    if (word) resolved[type] = word;
  }
  return resolved;
}

const wordFor = (links: Links, type: PermalinkPageType): string =>
  links?.[type] || DEFAULT_PERMALINKS[type];

/** `/treks/everest-base-camp-trek/` — slash-terminated, matching `trailingSlash: true`. */
export function permalinkPath(
  links: Links,
  type: PermalinkPageType,
  ...rest: string[]
): string {
  return `/${[wordFor(links, type), ...rest].join("/")}/`;
}

/** The word as a display label: `treks` → `Treks`, `day-trips` → `Day trips`. */
export function permalinkLabel(links: Links, type: PermalinkPageType): string {
  const words = wordFor(links, type).replaceAll("-", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The page types whose word differs from their default. */
export function customPermalinkTypes(links: Links): PermalinkPageType[] {
  return PERMALINK_PAGE_TYPES.filter(
    (type) => wordFor(links, type) !== DEFAULT_PERMALINKS[type],
  );
}

/** Swaps `pathname`'s first segment through `from` → `to` for each custom type; unchanged when none matches. */
function swapCustomSegment(
  links: Links,
  pathname: string,
  entry: (type: PermalinkPageType) => [string, string],
): string {
  const match = /^\/([^/?#]+)(.*)$/.exec(pathname);
  const to =
    match && new Map(customPermalinkTypes(links).map(entry)).get(match[1]!);
  return to ? `/${to}${match![2]}` : pathname;
}

/**
 * The route that serves a public address: `/treks/x/` → `/tours/x/`, since the
 * page folders keep the default names for every agency. Unchanged when the
 * address does not start with one of this agency's custom words.
 */
export function toInternalPath(links: Links, pathname: string): string {
  return swapCustomSegment(links, pathname, (type) => [
    wordFor(links, type),
    DEFAULT_PERMALINKS[type],
  ]);
}

/**
 * The public address for a default-word path: `/tours/x/` → `/treks/x/` once
 * the agency changed the Tour Page word. Unchanged when that page type still
 * uses its default. Serves both the old-address redirect and links saved before the
 * change (menu items store the default-word form).
 */
export function toPublicPath(links: Links, pathname: string): string {
  return swapCustomSegment(links, pathname, (type) => [
    DEFAULT_PERMALINKS[type],
    wordFor(links, type),
  ]);
}
